import React, {useEffect, useState} from 'react';
import {Box, Button, Card, CardContent, CardHeader, Divider, Grid, Stack, TextField, Typography} from '@mui/material';
import {useAuth} from '../auth-context';
import {useClient} from '../core/client';
import {isPasskeySupported, registerPasskey} from '../passkey/PasskeyService';

const FALLBACK_VALUE = 'N/A';

const pickValue = (source, keys, fallback = FALLBACK_VALUE) => {
    if (!source) return fallback;
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }
    return fallback;
};

const InfoRow = ({label, value}) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
);

const Account = () => {
    const { user, customer, setCustomer } = useAuth();
    const client = useClient();
    const [portalError, setPortalError] = useState('');
    const [portalLoading, setPortalLoading] = useState(false);
    const [planLabel, setPlanLabel] = useState('');
    const [planLoading, setPlanLoading] = useState(false);
    const [passkeySupported, setPasskeySupported] = useState(false);
    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const [passkeyError, setPasskeyError] = useState('');
    const [passkeySuccess, setPasskeySuccess] = useState('');
    const [reminderTime, setReminderTime] = useState('');
    const [reminderSaving, setReminderSaving] = useState(false);
    const [reminderError, setReminderError] = useState('');
    const [reminderSuccess, setReminderSuccess] = useState('');
    const [accountUsers, setAccountUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [newUserForm, setNewUserForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
    });
    const [newUserSaving, setNewUserSaving] = useState(false);
    const [newUserError, setNewUserError] = useState('');
    const [newUserSuccess, setNewUserSuccess] = useState('');

    const companyName = pickValue(customer || user, ['company', 'companyName', 'name', 'organization', 'organizationName']);
    const rawPlan = pickValue(customer, ['plan', 'planName', 'subscription', 'tier']);
    const plan = planLabel || (rawPlan === FALLBACK_VALUE ? 'View in portal' : rawPlan);
    const customerId = pickValue(customer, ['id', 'customerId', 'customer_id']);

    const handleManagePlan = async () => {
        setPortalError('');
        setPortalLoading(true);
        try {
            const { data } = await client.post('/auth/billing/portal');
            if (data?.url) {
                window.location.assign(data.url);
            } else {
                setPortalError('Unable to open the billing portal.');
            }
        } catch (err) {
            setPortalError('Unable to open the billing portal.');
        } finally {
            setPortalLoading(false);
        }
    };

    useEffect(() => {
        const loadPlan = async () => {
            setPlanLoading(true);
            try {
                const { data } = await client.get('/auth/billing/subscription');
                if (data?.planName) {
                    const parts = [data.planName];
                    if (data.interval) parts.push(`(${data.interval})`);
                    setPlanLabel(parts.join(' '));
                } else if (data?.status) {
                    setPlanLabel(`Status: ${data.status}`);
                } else {
                    setPlanLabel('');
                }
            } catch (err) {
                setPlanLabel('');
            } finally {
                setPlanLoading(false);
            }
        };
        loadPlan();
    }, [client]);

    useEffect(() => {
        let isMounted = true;
        const checkSupport = async () => {
            const supported = await isPasskeySupported();
            if (isMounted) setPasskeySupported(supported);
        };
        checkSupport();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (customer?.checkinReminderTime) {
            setReminderTime(customer.checkinReminderTime);
        }
    }, [customer]);

    useEffect(() => {
        let mounted = true;
        const loadAccountUsers = async () => {
            setUsersLoading(true);
            setUsersError('');
            try {
                const { data } = await client.get('/users/account');
                if (mounted) {
                    setAccountUsers(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (mounted) {
                    setUsersError('Unable to load account users.');
                }
            } finally {
                if (mounted) {
                    setUsersLoading(false);
                }
            }
        };
        loadAccountUsers();
        return () => {
            mounted = false;
        };
    }, [client]);

    const handleSaveReminderTime = async () => {
        setReminderError('');
        setReminderSuccess('');
        setReminderSaving(true);
        try {
            const { data } = await client.put('/customer/checkin-reminder-time', {
                checkinReminderTime: reminderTime || null,
            });
            if (data) {
                localStorage.setItem('auth_customer', JSON.stringify(data));
                setCustomer?.(data);
            }
            setReminderSuccess('Reminder time updated.');
        } catch (err) {
            setReminderError('Unable to update reminder time.');
        } finally {
            setReminderSaving(false);
        }
    };

    const handleRegisterPasskey = async () => {
        setPasskeyError('');
        setPasskeySuccess('');
        setPasskeyLoading(true);
        try {
            await registerPasskey(client);
            setPasskeySuccess('Passkey added successfully.');
        } catch (error) {
            const isCancelled = error?.name === 'AbortError' || error?.name === 'NotAllowedError';
            setPasskeyError(
                isCancelled
                    ? 'Passkey registration was cancelled.'
                    : 'Unable to add a passkey. Please try again.'
            );
        } finally {
            setPasskeyLoading(false);
        }
    };

    const handleNewUserFieldChange = (field) => (event) => {
        setNewUserForm((prev) => ({ ...prev, [field]: event.target.value }));
        setNewUserError('');
        setNewUserSuccess('');
    };

    const handleAddUser = async () => {
        setNewUserError('');
        setNewUserSuccess('');
        if (!newUserForm.email.trim() || !newUserForm.password.trim()) {
            setNewUserError('Email and password are required.');
            return;
        }
        setNewUserSaving(true);
        try {
            const { data } = await client.post('/users/account', {
                firstName: newUserForm.firstName,
                lastName: newUserForm.lastName,
                email: newUserForm.email,
                phone: newUserForm.phone,
                password: newUserForm.password,
            });
            setAccountUsers((prev) => {
                const next = [data, ...prev];
                return next.sort((a, b) => String(a?.email || '').localeCompare(String(b?.email || '')));
            });
            setNewUserForm({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                password: '',
            });
            setNewUserSuccess('User added to your account.');
        } catch (err) {
            setNewUserError(err?.response?.data?.message || 'Unable to add user.');
        } finally {
            setNewUserSaving(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Account
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Company" />
                        <CardContent>
                            <Stack spacing={1.5} divider={<Divider flexItem />}>
                                <InfoRow label="Organization" value={companyName} />
                                <InfoRow label="Plan" value={planLoading ? 'Loading...' : plan} />
                                <InfoRow label="Customer ID" value={customerId} />
                            </Stack>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleManagePlan}
                                    disabled={portalLoading}
                                >
                                    {portalLoading ? 'Opening portal...' : 'Manage plan'}
                                </Button>
                                {portalError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        {portalError}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Security" />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Add a passkey to sign in faster with Face ID, Touch ID, or Windows Hello.
                            </Typography>
                            {passkeySupported ? (
                                <Button
                                    variant="contained"
                                    onClick={handleRegisterPasskey}
                                    disabled={passkeyLoading}
                                >
                                    {passkeyLoading ? 'Waiting for passkey...' : 'Add passkey'}
                                </Button>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Passkeys are not supported in this browser.
                                </Typography>
                            )}
                            {passkeyError && (
                                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                    {passkeyError}
                                </Typography>
                            )}
                            {passkeySuccess && (
                                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                    {passkeySuccess}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Check-in Reminders" />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Set the time of day to send a push reminder for due check-ins.
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Reminder time"
                                    type="time"
                                    value={reminderTime}
                                    onChange={(event) => setReminderTime(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSaveReminderTime}
                                    disabled={reminderSaving}
                                >
                                    {reminderSaving ? 'Saving...' : 'Save reminder time'}
                                </Button>
                                {reminderError ? (
                                    <Typography variant="body2" color="error">
                                        {reminderError}
                                    </Typography>
                                ) : null}
                                {reminderSuccess ? (
                                    <Typography variant="body2" color="success.main">
                                        {reminderSuccess}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader title="Users" />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Users on this account
                                    </Typography>
                                    <Stack spacing={1} divider={<Divider flexItem />}>
                                        {usersLoading ? (
                                            <Typography variant="body2" color="text.secondary">Loading users...</Typography>
                                        ) : accountUsers.length ? (
                                            accountUsers.map((acctUser) => {
                                                const name = [acctUser?.first, acctUser?.fist, acctUser?.last].filter(Boolean).join(' ').trim() || 'Unnamed user';
                                                return (
                                                    <Box key={acctUser?.id || acctUser?._id || acctUser?.email} sx={{ py: 0.5 }}>
                                                        <Typography variant="body1">{name}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {acctUser?.email || FALLBACK_VALUE}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No users found.</Typography>
                                        )}
                                    </Stack>
                                    {usersError && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                            {usersError}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Add user to this account
                                    </Typography>
                                    <Stack spacing={2}>
                                        <TextField
                                            label="First name"
                                            value={newUserForm.firstName}
                                            onChange={handleNewUserFieldChange('firstName')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Last name"
                                            value={newUserForm.lastName}
                                            onChange={handleNewUserFieldChange('lastName')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Email"
                                            type="email"
                                            value={newUserForm.email}
                                            onChange={handleNewUserFieldChange('email')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Phone"
                                            value={newUserForm.phone}
                                            onChange={handleNewUserFieldChange('phone')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Temporary password"
                                            type="password"
                                            value={newUserForm.password}
                                            onChange={handleNewUserFieldChange('password')}
                                            fullWidth
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddUser}
                                            disabled={newUserSaving}
                                        >
                                            {newUserSaving ? 'Adding user...' : 'Add user'}
                                        </Button>
                                        {newUserError ? (
                                            <Typography variant="body2" color="error">
                                                {newUserError}
                                            </Typography>
                                        ) : null}
                                        {newUserSuccess ? (
                                            <Typography variant="body2" color="success.main">
                                                {newUserSuccess}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Account;
